// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// D-POT-7 — the Plan of Treatment tab: one worklist, four sub-tabs in workflow order (Queue → In
// progress → Completed → Voided). The server decides which sub-tab a visit is in, pages, sorts and
// filters (GET carerx/pot/worklist); the tiles and facets come from GET carerx/pot/worklist/summary.
// Replaces the stacked staff queue + all-nurses dashboard (CareRxPotQueue / CareRxPotDashboard),
// which listed a claimed visit with a draft twice and silently capped at 200 / 500 rows.
// The server scopes rows by role (a nurse: her own, shared and the unclaimed pool), so one component
// serves the staff POT tab and the nurse's My visits; only the transfer actions differ by role.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import { ClipboardList, Plus, RefreshCw } from 'lucide-react'
import { claimCareRxPot, fetchSignedCareRxEsign, openCareRxDocument } from '../../api/client'
import type { PotWorklistRow, PotWorklistView } from '../../api/carerxPot'
import { formatUsDate } from '../../api/format'
import { useAuth } from '../../auth/AuthContext'
import SearchableSelect from '../../components/common/SearchableSelect'
import { Pager, preferredPageSize, rememberPageSize } from '../../components/Pager'
import type { MenuAction } from '../../components/ui/ActionMenu'
import ActionMenu from '../../components/ui/ActionMenu'
import AppButton from '../../components/ui/AppButton'
import AppDialog from '../../components/ui/AppDialog'
import AppTabs from '../../components/ui/AppTabs'
import DateInput from '../../components/ui/DateInput'
import { AppTextField, SelectField } from '../../components/ui/FormFields'
import IconAction from '../../components/ui/IconAction'
import LoadState from '../../components/ui/LoadState'
import PageHeader from '../../components/ui/PageHeader'
import Section, { StatTile } from '../../components/ui/Section'
import { useToast } from '../../components/ui/Toast'
import { useClaimPot, usePotWorklist, usePotWorklistSummary, useRefreshPotEnvelope, useSchedulePotVisit, useStartPot } from '../../hooks/queries/carerxPot'
import { SecurityTraining } from '../pot/overlays/SecurityTraining'
import { usePotPharmacyName } from '../pot/PotShell'
import { apiErrorMessage } from './carerxPotRequest'
import CancelVisitButton from './CancelVisitButton'
import CareRxPotManualStartDialog from './CareRxPotManualStartDialog'
import CareRxPotRowDialogs, { type PotDlg } from './CareRxPotRowDialogs'
import CareRxPotSendDialog from './CareRxPotSendDialog'
import { CompletedTable, InProgressTable, QueueTable, VoidedTable, type SelectionProps } from './CareRxPotTables'
import {
  DATE_OPTIONS, asPotRow, defaultSort, isWorklistView, potFormPath, serverSort, sinceIso, todayIso, worklistSortOptions,
  type PotDateFilter, type PotRow, type PotWorklistSortChoice,
} from './carerxPotDashboardLogic'

interface Filters { search: string; date: PotDateFilter; nurse: string; patient: string; drug: string }
const NO_FILTERS: Filters = { search: '', date: 'all', nurse: '', patient: '', drug: '' }
const active = (f: Filters) => !!(f.search || f.date !== 'all' || f.nurse || f.patient || f.drug)

/** Row selection over the rows on screen (the current page). */
function useSelection(visible: PotRow[]): SelectionProps & { clear: () => void; ids: string[] } {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  return {
    selected, ids: Array.from(selected),
    onToggle: (id, on) => setSelected(s => { const n = new Set(s); if (on) n.add(id); else n.delete(id); return n }),
    onToggleAll: on => setSelected(on ? new Set(visible.map(r => r.visit_id)) : new Set()),
    clear: () => setSelected(new Set()),
  }
}

export default function CareRxPotWorklist() {
  const nav = useNavigate()
  const toast = useToast()
  const { role } = useAuth()
  const isNurse = role === 'provider_nurse'
  // Reassign… / Share… are staff/admin endpoints (a nurse login gets 403) — offered only where they work.
  const canTransfer = !isNurse
  const pharmacyName = usePotPharmacyName()

  // ── the sub-tab lives in the URL (?view=), so a link opens the same list ──
  const [params, setParams] = useSearchParams()
  const view: PotWorklistView = isWorklistView(params.get('view')) ? params.get('view') as PotWorklistView : 'queue'
  const setView = useCallback((v: PotWorklistView) => setParams(p => { const n = new URLSearchParams(p); n.set('view', v); return n }, { replace: true }), [setParams])

  const [f, setF] = useState<Filters>(NO_FILTERS)
  const upd = (p: Partial<Filters>) => setF(x => ({ ...x, ...p }))
  const [sortChoice, setSortChoice] = useState<PotWorklistSortChoice>(defaultSort(view))
  const [page, setPage] = useState(1)
  const [pageSize, setPageSizeRaw] = useState(() => preferredPageSize(25))
  const setPageSize = (n: number) => { rememberPageSize(n); setPageSizeRaw(n) }

  // Search is debounced: one request per pause, not per keystroke.
  const [q, setQ] = useState('')
  useEffect(() => { const t = setTimeout(() => setQ(f.search.trim()), 300); return () => clearTimeout(t) }, [f.search])
  // A new view starts on its own default sort; any filter / sort / size change goes back to page 1.
  useEffect(() => { setSortChoice(defaultSort(view)) }, [view])
  useEffect(() => { setPage(1) }, [view, q, f.date, f.nurse, f.patient, f.drug, sortChoice, pageSize])

  const searchRef = useRef<HTMLInputElement | null>(null)
  // ⌘K / Ctrl+K focuses the search box; Esc clears it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); searchRef.current?.focus() }
      if (e.key === 'Escape' && document.activeElement === searchRef.current) upd({ search: '' })
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const summary = usePotWorklistSummary()
  const canVoid = summary.data?.can_void ?? false
  // A shared link to Voided opened by an account that cannot void lands on the Queue.
  useEffect(() => { if (view === 'voided' && summary.data && !canVoid) setView('queue') }, [view, summary.data, canVoid, setView])

  const { sort, dir } = serverSort(sortChoice)
  const list = usePotWorklist({
    view, page, pageSize, sort, dir, q: q || undefined, since: sinceIso(f.date),
    nurse: f.nurse || undefined, patient: f.patient || undefined, drug: f.drug || undefined,
  })
  const rows = useMemo(() => list.data?.items ?? [], [list.data])
  const potRows = useMemo(() => rows.filter(r => r.visit_id).map(asPotRow), [rows])
  const total = list.data?.total ?? 0
  const selDrafts = useSelection(view === 'in_progress' ? potRows : [])
  const selDone = useSelection(view === 'completed' ? potRows : [])
  useEffect(() => { selDrafts.clear(); selDone.clear() }, [view, page])   // eslint-disable-line react-hooks/exhaustive-deps

  const claim = useClaimPot()
  const start = useStartPot()
  // staff pick the visit date when they start a POT for a nurse (a nurse starting her own POT starts today)
  const [startFor, setStartFor] = useState<{ row: PotWorklistRow; date: string } | null>(null)
  const schedule = useSchedulePotVisit()
  const refreshEnvelope = useRefreshPotEnvelope()
  const [dlg, setDlg] = useState<PotDlg>(null)
  const [send, setSend] = useState<PotRow | null>(null)
  const [manualOpen, setManualOpen] = useState(false)
  const refetchAll = () => { void list.refetch(); void summary.refetch() }

  const openForm = useCallback((id: string, viewOnly = false) => nav(potFormPath(id, viewOnly)), [nav])
  const workingPdf = (r: PotRow) => { if (r.document_id) void openCareRxDocument(r.document_id) }
  const signedPdf = async (r: PotRow) => {
    if (!r.envelope_id) return
    try { const d = await fetchSignedCareRxEsign(r.envelope_id); await openCareRxDocument(d.document_id) }
    catch (e) { toast.show(apiErrorMessage(e), 'error') }
  }
  // Claim succeeds even with open staffing blockers and names them in `note` (CareRxPotFunctions.Claim, B-2).
  const claimVisit = async (r: PotWorklistRow) => {
    if (claim.isPending || !r.visit_id) return
    try {
      const res = await claim.mutateAsync(r.visit_id)
      const who = r.patient_name ?? 'the visit'
      toast.show(res.note ? `Claimed ${who} — ${res.note}` : `Claimed ${who}.`, res.note ? 'info' : 'success')
    } catch (e) {
      const b = (e as { body?: { error?: string; nurse?: string } }).body
      toast.show(b?.error === 'claimed_by_another' ? `Already claimed by ${b.nurse}` : apiErrorMessage(e, 'Claim failed'), 'error')
    }
  }
  // Ready → a visit. A nurse starting it takes it and opens the form (POT classic's "+ New Assessment");
  // staff schedule it for today and it waits in the Queue, unclaimed.
  const startReady = async (r: PotWorklistRow, visitDate = todayIso()) => {
    if (start.isPending || !r.therapy_id) return
    try {
      const v = await start.mutateAsync({ therapyId: r.therapy_id, visitDate })
      if (isNurse) { await claimCareRxPot(v.visit_id); openForm(v.visit_id); return }
      toast.show(`Started a POT for ${r.patient_name ?? 'the patient'} — it is in the Queue, unclaimed.`, 'success')
    } catch (e) { toast.show(apiErrorMessage(e, 'Could not start the POT'), 'error') }
  }
  const refreshStatus = async (r: PotRow) => {
    if (!r.envelope_id) return
    try { const d = await refreshEnvelope.mutateAsync(r.envelope_id); toast.show(`Signature status: ${d.status}`, 'info') }
    catch (e) { toast.show(apiErrorMessage(e), 'error') }
  }

  // ── row actions ──
  // P0 B2: a nurse charts only a visit that is hers or shared with her (the server returns no one else's).
  const canChart = (r: PotWorklistRow) => !isNurse || !!r.nurse_id
  const queueActions = (r: PotWorklistRow): ReactNode => {
    if (r.kind === 'ready') return <AppButton variant="contained" size="small" disabled={start.isPending}
      onClick={() => { if (isNurse) void startReady(r); else setStartFor({ row: r, date: todayIso() }) }}>Start POT</AppButton>
    const pr = asPotRow(r)
    const menu: MenuAction[] = []
    if (canTransfer) menu.push({ key: 'reassign', label: r.nurse_id ? 'Reassign…' : 'Assign…', onClick: () => setDlg({ kind: 'reassign', row: pr }) })
    if (canTransfer) menu.push({ key: 'share', label: 'Share…', onClick: () => setDlg({ kind: 'share', row: pr }) })
    return <>
      {r.status === 'scheduled' && <AppButton variant={r.nurse_id ? 'outlined' : 'contained'} size="small" disabled={claim.isPending} title="Assign this POT to yourself" onClick={() => { void claimVisit(r) }}>{claim.isPending && claim.variables === r.visit_id ? 'Claiming…' : 'Claim'}</AppButton>}
      {canChart(r) && <AppButton variant="outlined" size="small" onClick={() => openForm(r.visit_id!)}>Draft POT</AppButton>}
      <CancelVisitButton visitId={r.visit_id!} label={`${r.patient_name ?? 'Patient'} · ${r.visit_type ?? 'visit'}`} onCancelled={refetchAll} compact />
      {menu.length > 0 && <ActionMenu label="More actions" actions={menu} />}
    </>
  }
  const dateCell = (r: PotWorklistRow): ReactNode => r.visit_date
    ? <b>{formatUsDate(r.visit_date)}</b>
    // mig 334: a delivery-created visit is born unscheduled — the date is set right on the row.
    // DateInput commits once per complete date (ASP 2026-10-14 #2), so a half-typed year never saves.
    : <DateInput label="Schedule visit date" onChange={iso => iso && r.visit_id
        ? schedule.mutateAsync({ visitId: r.visit_id, visitDate: iso }).catch(e => { toast.show(apiErrorMessage(e, 'Schedule failed'), 'error'); throw e })
        : undefined} />
  const draftMenu = (r: PotRow): MenuAction[] => {
    const items: MenuAction[] = []
    if (canTransfer && (r.status === 'scheduled' || r.status === 'claimed')) items.push({ key: 'reassign', label: 'Reassign…', onClick: () => setDlg({ kind: 'reassign', row: r }) })
    if (canTransfer) items.push({ key: 'share', label: 'Share…', onClick: () => setDlg({ kind: 'share', row: r }) })
    items.push({ key: 'delete', label: 'Delete', danger: true, dividerBefore: items.length > 0, onClick: () => setDlg({ kind: 'delete', row: r }) })
    return items
  }
  const doneMenu = (r: PotRow): MenuAction[] => {
    const items: MenuAction[] = [{ key: 'view', label: 'View', onClick: () => openForm(r.visit_id, true) }]
    if (r.signature === 'awaiting_physician') items.push({ key: 'refresh', label: 'Refresh status', onClick: () => { void refreshStatus(r) } })
    if (r.signature !== 'voided') items.push({ key: 'dup', label: 'Duplicate', onClick: () => setDlg({ kind: 'duplicate', row: r }) })
    if (canTransfer && r.signature !== 'voided') items.push({ key: 'share', label: 'Share…', onClick: () => setDlg({ kind: 'share', row: r }) })
    if (canVoid && r.signature !== 'awaiting_physician' && r.signature !== 'voided') items.push({ key: 'void', label: 'Void…', danger: true, dividerBefore: true, onClick: () => setDlg({ kind: 'void', row: r }) })
    return items
  }
  const doneButtons = (r: PotRow): ReactNode => {
    const working = r.document_id ? <AppButton variant="outlined" size="small" onClick={() => workingPdf(r)}>Working PDF</AppButton> : null
    if (r.signature === 'awaiting_physician') return <><AppButton variant="outlined" color="error" size="small" onClick={() => setDlg({ kind: 'revoke', row: r })}>Revoke</AppButton>{working}</>
    if (r.signature === 'signed') return <><AppButton variant="contained" color="success" size="small" onClick={() => { void signedPdf(r) }}>Signed PDF</AppButton>{working}</>
    if (r.signature === 'voided') return working
    const canSend = !!r.document_id
    return <><AppButton variant="contained" size="small" disabled={!canSend} title={canSend ? 'Send the POT to the physician for e-signature' : 'The POT document has not been generated yet'} onClick={() => setSend(r)}>Send for signature</AppButton>{working}</>
  }

  // ── tiles + tabs (server counts — the workload, unfiltered) ──
  const counts = summary.data?.counts
  const tile = (label: string, value: ReactNode, target?: PotWorklistView) => (
    <Box role={target ? 'button' : undefined} tabIndex={target ? 0 : undefined} aria-pressed={target ? view === target : undefined} onClick={target ? () => setView(target) : undefined}
      onKeyDown={e => { if (target && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setView(target) } }}
      sx={{ flex: '1 1 150px', display: 'flex', cursor: target ? 'pointer' : 'default', borderRadius: 1, outline: target && view === target ? '2px solid var(--color-primary)' : 'none', '& > *': { width: '100%', bgcolor: target && view === target ? 'var(--color-primary-soft)' : undefined } }}>
      <StatTile label={label} value={value} />
    </Box>
  )
  const n = (v: PotWorklistView) => counts ? String(counts[v]) : '…'
  const tabs = [
    { value: 'queue', label: 'Queue', badge: n('queue') },
    { value: 'in_progress', label: 'In progress', badge: n('in_progress') },
    { value: 'completed', label: 'Completed', badge: n('completed') },
    ...(canVoid ? [{ value: 'voided', label: 'Voided', badge: n('voided') }] : []),
  ]
  const facets = summary.data?.facets
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1
  const bulkBar = (count: number, what: string, action: ReactNode, onClear: () => void) => count > 0 && (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', fontSize: 13, bgcolor: 'var(--color-primary-soft)', border: '1px solid var(--color-primary)', borderRadius: 1, px: 1.5, py: 0.75, mb: 1.25 }}>
      <b>{count} {what} selected</b>{action}<AppButton size="small" onClick={onClear}>Clear</AppButton>
    </Box>
  )
  const pager = <Pager page={page} totalPages={list.data?.total_pages ?? 1} total={total} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} label="visits" />

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <SecurityTraining />
      <PageHeader icon={<ClipboardList size={16} />} title="Plan of Treatment"
        subtitle={isNurse ? 'Your queue, drafts and completed assessments.' : 'Every nurse\'s POTs, from the queue to physician signature.'}
        actions={<>
          <IconAction label="Refresh" icon={<RefreshCw size={15} />} onClick={refetchAll} />
          {!isNurse && <AppButton variant="contained" size="small" startIcon={<Plus size={14} />} onClick={() => setManualOpen(true)}>Start a POT</AppButton>}
        </>} />

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {tile('Queue', counts?.queue ?? '…', 'queue')}
        {tile('In progress', counts?.in_progress ?? '…', 'in_progress')}
        {tile('Completed', counts?.completed ?? '…', 'completed')}
        {tile('Avg. time', summary.data ? `${summary.data.avg_minutes} min` : '…')}
      </Box>

      <AppTabs value={view} onChange={v => setView(v as PotWorklistView)} tabs={tabs} ariaLabel="Plan of Treatment stages" />

      <Section title="Find a POT">
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1, alignItems: 'center' }}>
          <AppTextField size="small" inputRef={searchRef} value={f.search} onChange={e => upd({ search: e.target.value })} placeholder="Search patient, MRN, drug or diagnosis… (⌘K)" inputProps={{ 'aria-label': 'Search' }} sx={{ gridColumn: 'span 2' }} />
          <SelectField size="small" label="Date" value={f.date} onChange={v => upd({ date: v as PotDateFilter })} options={DATE_OPTIONS} />
          {!isNurse && <SearchableSelect ariaLabel="Nurse" value={f.nurse} onChange={v => upd({ nurse: v })} placeholder="All nurses" options={[{ value: '', label: 'All nurses' }, ...(facets?.nurses ?? []).map(x => ({ value: x.id, label: x.name ?? 'Unknown' }))]} />}
          <SearchableSelect ariaLabel="Patient" value={f.patient} onChange={v => upd({ patient: v })} placeholder="All patients" options={[{ value: '', label: 'All patients' }, ...(facets?.patients ?? []).map(x => ({ value: x.id, label: x.name ?? 'Unknown' }))]} />
          <SearchableSelect ariaLabel="Drug" value={f.drug} onChange={v => upd({ drug: v })} placeholder="All drugs" options={[{ value: '', label: 'All drugs' }, ...(facets?.drugs ?? []).map(x => ({ value: x, label: x }))]} />
          <SelectField size="small" label="Sort" value={sortChoice} onChange={v => setSortChoice(v as PotWorklistSortChoice)} options={worklistSortOptions(view)} />
          {active(f) && <AppButton size="small" onClick={() => setF(NO_FILTERS)}>Clear filters</AppButton>}
        </Box>
        <Box sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }} aria-live="polite">
          {list.data ? <>Showing <b>{first}–{first + rows.length - (rows.length ? 1 : 0)}</b> of <b>{total}</b>{active(f) ? ' matching' : ''}</> : ' '}
        </Box>
      </Section>

      <LoadState query={list} what="the Plan of Treatment list" isEmpty={() => false} empty={null}>
        {() => <Box sx={{ opacity: list.isPlaceholderData ? 0.6 : 1, transition: 'opacity .15s' }}>
          {view === 'queue' && (
            <Section title="Queue" subtitle="Ready to start, unclaimed, and claimed but not started. Opening the form moves a visit to In progress.">
              <QueueTable rows={rows} total={counts?.queue ?? total} dateCell={dateCell} actionsFor={queueActions} />
            </Section>
          )}
          {view === 'in_progress' && (
            <Section title="In progress">
              {bulkBar(selDrafts.selected.size, 'drafts', <AppButton variant="outlined" color="error" size="small" onClick={() => setDlg({ kind: 'bulkDelete', ids: selDrafts.ids })}>Delete selected</AppButton>, selDrafts.clear)}
              <InProgressTable rows={potRows} total={counts?.in_progress ?? total} sel={selDrafts} onResume={r => openForm(r.visit_id)} menuFor={draftMenu} />
            </Section>
          )}
          {view === 'completed' && (
            <Section title="Completed assessments">
              {bulkBar(selDone.selected.size, 'assessments', null, selDone.clear)}
              <CompletedTable rows={potRows} total={counts?.completed ?? total} sel={selDone} buttonsFor={doneButtons} menuFor={doneMenu} />
            </Section>
          )}
          {view === 'voided' && canVoid && (
            <Section title="Voided assessments">
              <VoidedTable rows={potRows} total={counts?.voided ?? total} onWorkingPdf={workingPdf} onView={r => openForm(r.visit_id, true)} />
            </Section>
          )}
          <Box sx={{ mt: 1 }}>{pager}</Box>
        </Box>}
      </LoadState>

      <AppDialog open={startFor !== null} title="Start a POT" maxWidth="xs" busy={start.isPending} onClose={() => setStartFor(null)}
        actions={<>
          <AppButton onClick={() => setStartFor(null)} disabled={start.isPending}>Cancel</AppButton>
          <AppButton variant="contained" disabled={start.isPending || !startFor?.date}
            onClick={() => { const s = startFor; if (s) void startReady(s.row, s.date).then(() => setStartFor(null)) }}>Start POT</AppButton>
        </>}>
        <Box sx={{ fontSize: 13, mb: 1.5 }}>{startFor?.row.patient_name} · {startFor?.row.drug_name}. The visit goes to the Queue unclaimed; assign a nurse from its menu.</Box>
        <DateInput label="Visit date" value={startFor?.date ?? ''} onChange={v => setStartFor(s => (s ? { ...s, date: v ?? '' } : s))} />
      </AppDialog>
      <CareRxPotRowDialogs dlg={dlg} onClose={() => setDlg(null)} onBulkDeleted={selDrafts.clear} onOpenForm={id => openForm(id)} pharmacyName={pharmacyName} />
      {send && <CareRxPotSendDialog row={send} onClose={() => setSend(null)} onSent={() => { setSend(null); refetchAll() }} />}
      <CareRxPotManualStartDialog open={manualOpen} onClose={() => setManualOpen(false)} onStarted={() => { setManualOpen(false); setView('queue'); toast.show('POT started — it is in the Queue.', 'success') }} />
    </Box>
  )
}
